import { useMemo } from "react";
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
} from "@chakra-ui/react";
import { WarningIcon } from "@chakra-ui/icons";
import { FieldError, useController, UseFormReturn } from "react-hook-form";
import { SkuComponentType } from "__generated__/gql/graphql";
import { FieldsetWell } from "components/Shared/Forms/Fieldset";
import type { SkuInputs } from "components/Products/SkuPanel/utils";
import { INNER_CASE_QUANTITY_LABEL } from "utils/schemas/productLine";
import { SKU_COMPONENT_TYPE_LABELS, SKU_COMPONENT_TYPES } from "./utils";
import SkuComponent from "./SkuComponent";

const DISABLED_MESSAGE_BY_TYPE: Partial<Record<SkuComponentType, string>> = {
  [SkuComponentType.INNER]: `Provide an ${INNER_CASE_QUANTITY_LABEL} for the product line if inner cases are applicable for this SKU.`,
};

export type IsUpcPresentByType = Partial<Record<SkuComponentType, boolean>>;

type Props = {
  disabledSkuComponentTypes?: SkuComponentType[];
  formContext: UseFormReturn<SkuInputs>;
  idEditKey?: string;
  isUpcPresentByType: IsUpcPresentByType | undefined | null;
};

export default function SkuComponents({
  disabledSkuComponentTypes,
  formContext,
  idEditKey,
  isUpcPresentByType,
}: Props) {
  const { control } = formContext;
  const {
    field: { value },
    fieldState: { error },
  } = useController({ control, name: "skuComponents" });

  const arrIdxByType = useMemo(
    () =>
      value?.reduce<Partial<Record<SkuComponentType, number>>>(
        (acc, v, idx) => {
          if (!v.skuComponentType) return acc;
          acc[v.skuComponentType] = idx;
          return acc;
        },
        {},
      ),
    [value],
  );

  return (
    <Tabs
      variant="enclosed-colored"
      colorScheme="lightPinkButton"
      size="sm"
      my={8}
    >
      <TabList mb={0} borderBottom={0}>
        {SKU_COMPONENT_TYPES.map((sct) => {
          const fieldIdx = arrIdxByType?.[sct];
          const tabError =
            error && Array.isArray(error) && typeof fieldIdx === "number"
              ? (error as FieldError[])[fieldIdx]
              : undefined;
          return (
            <Tooltip
              key={sct}
              isDisabled={
                !!disabledSkuComponentTypes &&
                !disabledSkuComponentTypes.includes(sct)
              }
              label={DISABLED_MESSAGE_BY_TYPE[sct]}
              hasArrow
              placement="top"
              openDelay={500}
              textAlign="center"
            >
              <Tab
                key={sct}
                isDisabled={disabledSkuComponentTypes?.includes(sct)}
                fontFamily="heading"
                fontWeight="500"
                fontSize="md"
                pb=".5em"
                px={3}
                borderTopWidth={2}
                minW={0}
                sx={{
                  "--tabs-bg": "var(--chakra-colors-gray-100)",
                  "--tabs-color": "var(--chakra-colors-gray-500)",
                  borderBottomColor: "var(--chakra-colors-gray-300)",
                }}
                _selected={{
                  "--tabs-bg": "var(--chakra-colors-gray-200)",
                  "--tabs-color": "var(--chakra-colors-magenta-veryDark)",
                  borderColor: "inherit",
                  borderTopColor: "currentColor",
                  borderBottomColor: "transparent",
                  borderLeftColor: "var(--chakra-colors-gray-300)",
                  borderRightColor: "var(--chakra-colors-gray-300)",
                }}
              >
                <Box as="span" isTruncated>
                  {SKU_COMPONENT_TYPE_LABELS[sct]} Details
                </Box>
                {tabError && <WarningIcon color="red" ml={2} />}
              </Tab>
            </Tooltip>
          );
        })}
      </TabList>

      <FieldsetWell borderTopRadius={0} mt={0}>
        <TabPanels>
          {SKU_COMPONENT_TYPES.map((sct) => {
            const arrIdx = arrIdxByType?.[sct];
            return (
              <TabPanel key={sct} p={0}>
                {typeof arrIdx === "number" && (
                  <SkuComponent
                    key={`${sct}-${arrIdx}`}
                    idEditKey={idEditKey}
                    index={arrIdx}
                    isDisabled={
                      !!disabledSkuComponentTypes &&
                      disabledSkuComponentTypes.includes(sct)
                    }
                    isUpcPresent={!!isUpcPresentByType?.[sct]}
                  />
                )}
              </TabPanel>
            );
          })}
        </TabPanels>
      </FieldsetWell>
    </Tabs>
  );
}
